import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'

import tFormStepsFactory from '@/i18n/translations/components/formStepsFactory.json'
import { useAuthStore } from '@/stores/modules/auth'
import type {
  FactoryId,
  FormStepsFactory,
  Step,
} from '@/types/formStepsFactory'

const FAuthEmail = defineAsyncComponent(
  () => import('@/components/Form/Auth/Sso/FAuthEmail.vue'),
)
const FAuthOtp = defineAsyncComponent(
  () => import('@/components/Form/Auth/Sso/FAuthOtp.vue'),
)

const FAuthRegister = defineAsyncComponent(
  () => import('@/components/Form/Auth/Sso/FAuthRegister.vue'),
)

export const useStepsAuth = () => {
  const factoryId: FactoryId = 'auth'

  const authStore = useAuthStore()
  const { user, userHasCompletedSetup, dialogAuth } = storeToRefs(authStore)

  const { t } = useI18n({ ...tFormStepsFactory, useScope: 'global' })

  const steps = computed<Step[]>(() => [
    {
      avatar: { visible: false },
      component: () => FAuthEmail,
      enabled: !user.value,
      formId: 'auth-email',
      subtitle: t('subtitle.authLogin'),
      title: t('title.authLogin'),
    },
    {
      avatar: { visible: false },
      component: () => FAuthOtp,
      enabled: !user.value,
      formId: 'auth-otp',
      subtitle: (next) =>
        next.countdown ? t('subtitle.authOtp') : t('subtitle.authOtpExpired'),
      title: t('title.authOtp'),
    },
    {
      avatar: { visible: false },
      component: () => FAuthRegister,
      enabled: (next) => !user.value && next?.status === 417,
      formId: 'auth-register',
      subtitle: t('subtitle.authRegister'),
      title: t('title.authRegister'),
    },
  ])

  const stepsAuth = computed<Step[]>(() => {
    if (user.value && !userHasCompletedSetup.value) {
      return steps.value.slice(2, 3).map((step) => ({ ...step, enabled: true }))
    }

    return steps.value
  })

  watchEffect(() => {
    if (user.value && !userHasCompletedSetup.value) {
      dialogAuth.value = true
    }
  })

  const getFormStepsAuth = (): FormStepsFactory => stepsAuth

  return {
    factoryId,
    getFormStepsAuth,
  }
}
